import React, { useEffect, useState } from "react";
import { Settings as SettingsIcon, AlertCircle, Plus, Trash2, Save, ChevronDown, ChevronRight, Sparkles, ShieldCheck } from "lucide-react";
import Card from "../components/Card.jsx";
import Pill from "../components/Pill.jsx";
import { SkeletonSections } from "../components/Skeleton.jsx";
import InfoTip from "../components/InfoTip.jsx";
import GradientHeader from "../components/GradientHeader.jsx";
import ConfirmDialog, {
  isConfirmBypassed,
  getConfirmBypassUntil,
  clearConfirmBypass,
} from "../components/ConfirmDialog.jsx";
import { apiFetch, fetchApi } from "../lib/api.js";

// ============================================================================
// Stránka Pravidla a výjimky
// ----------------------------------------------------------------------------
// Centrální editor pravidel uložených v Netlify Blobs (store "pravidla").
// 7 sekcí (3 fakturační + 4 provozní). Každá sekce: collapsible, status badge,
// inline edit tabulky, add/delete řádků, save jako celá sekce.
//
// Status "Mirror": kód dnes čte vlastní hardcoded mirror. Refactor v týdnu 2.
// Změny aplikované v editoru zatím neovlivní běžící automatizace, ale jsou
// uložené jako single source of truth pro budoucí refactor.
// ============================================================================

// Schéma sekcí — určuje pořadí, popisek, ikona, sloupce tabulky.
const SECTIONS = [
  {
    key: "fakturacni_aliasy_jmena",
    title: "Aliasy klientů: překlepy a alternativní jména",
    icon: ShieldCheck,
    group: "fakturace",
    columns: [
      { field: "fapiJmeno", label: "FAPI jméno", type: "text", width: "w-48" },
      { field: "sheetKlient", label: "Klient v adminu", type: "text", width: "w-48" },
      { field: "poznamka", label: "Poznámka", type: "text" },
      { field: "aktivni", label: "Aktivní", type: "boolean", width: "w-20" },
    ],
    newRowDefaults: { fapiJmeno: "", sheetKlient: "", poznamka: "", aktivni: true },
  },
  {
    key: "fakturacni_aliasy_item_based",
    title: "Aliasy podle položky: firma fakturuje za více klientů",
    icon: ShieldCheck,
    group: "fakturace",
    customRender: true, // Speciální struktura (nested rules), render vlastní
  },
  {
    key: "fakturacni_dph_navyseni",
    title: "DPH navýšení (klienti fakturující přes s.r.o./a.s.)",
    icon: ShieldCheck,
    group: "fakturace",
    customRender: true, // Má 2 sub-listy (items + výjimky) plus vatRate
  },
  {
    key: "trenerske_sazby_vyjimky",
    title: "Sazby trenérů: individuální výjimky",
    icon: SettingsIcon,
    group: "provoz",
    columns: [
      { field: "trener", label: "Trenér", type: "text", width: "w-48" },
      { field: "sazba", label: "Sazba (Kč)", type: "number", width: "w-28" },
      { field: "duvod", label: "Důvod", type: "text" },
      { field: "platiOd", label: "Platí od", type: "date", width: "w-36" },
      { field: "aktivni", label: "Aktivní", type: "boolean", width: "w-20" },
    ],
    newRowDefaults: { trener: "", sazba: 0, duvod: "", platiOd: new Date().toISOString().slice(0, 10), aktivni: true },
  },
  {
    key: "bonusy_vyjimky_klientu",
    title: "Klienti vyloučení z bonusů (rodinné PT, atd.)",
    icon: SettingsIcon,
    group: "provoz",
    columns: [
      { field: "klient", label: "Klient", type: "text", width: "w-56" },
      { field: "duvod", label: "Důvod", type: "text" },
      { field: "platiOd", label: "Platí od", type: "date", width: "w-36" },
      { field: "aktivni", label: "Aktivní", type: "boolean", width: "w-20" },
    ],
    newRowDefaults: { klient: "", duvod: "", platiOd: new Date().toISOString().slice(0, 10), aktivni: true },
  },
  {
    key: "minima_treninku_dle_balicku",
    title: "Minimum tréninků za měsíc dle balíčku (1T/2T/3T/permanentka)",
    icon: SettingsIcon,
    group: "provoz",
    columns: [
      { field: "pattern", label: "Pattern (case insensitive)", type: "text", width: "w-48" },
      { field: "min", label: "Min za měsíc", type: "number", width: "w-32" },
      { field: "priorita", label: "Priorita", type: "number", width: "w-24" },
      { field: "poznamka", label: "Poznámka", type: "text" },
      { field: "aktivni", label: "Aktivní", type: "boolean", width: "w-20" },
    ],
    newRowDefaults: { pattern: "", min: 0, priorita: 1, poznamka: "", aktivni: true },
  },
  {
    key: "mapovani_balicku_treninky",
    title: "Počet tréninků v balíčku (Log Cash → Hotovost)",
    icon: SettingsIcon,
    group: "provoz",
    columns: [
      { field: "balicek", label: "Balíček", type: "text", width: "w-72" },
      { field: "treninku", label: "Počet tréninků", type: "number", width: "w-36" },
      { field: "aktivni", label: "Aktivní", type: "boolean", width: "w-20" },
    ],
    newRowDefaults: { balicek: "", treninku: 0, aktivni: true },
  },
];

// Generátor unikátních ID pro nové řádky (jednoduchý, založený na timestampu).
function newId() {
  return "n" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

// =====================================================================
// Hlavní stránka
// =====================================================================
export default function Pravidla() {
  const [data, setData] = useState(null);
  const [statusIndex, setStatusIndex] = useState({});
  const [initialized, setInitialized] = useState(false);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null); // section key during save
  const [expanded, setExpanded] = useState({});
  const [dirty, setDirty] = useState({}); // sectionKey → true když má neuložené změny
  // Confirm dialog pro Uložit sekci — ChránÍ proti omylem klepnutí na Save (změny se loguje do auditu).
  const [savePending, setSavePending] = useState(null); // { sectionKey, payload, sectionLabel }
  // Confirm dialog pro inicializaci pravidel z hardcoded mirror (jednorazova akce).
  const [initPending, setInitPending] = useState(false);

  // SWR-style cache: instant read z localStorage, refresh na pozadí.
  // Druhe a dalsi otevreni stranky = instant render bez cekání na network.
  const CACHE_KEY = "pravidla_snapshot_v1";

  const applySnapshot = (j) => {
    setInitialized(!!j.initialized);
    setStatusIndex(j.statusIndex || {});
    if (j.initialized) {
      setData(j.data || {});
      setDefaults(null);
    } else {
      setData(j.defaults || {});
      setDefaults(j.defaults || {});
    }
    // Default: vsechny sekce zavrene. User si rozkliva co potrebuje.
    setExpanded((s) => s);
  };

  const load = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setError(null);
    try {
      const j = await fetchApi("/api/pravidla");
      applySnapshot(j);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(j)); } catch { /* private mode */ }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 1) Rychlý paint z cache (pokud existuje)
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const j = JSON.parse(cached);
        applySnapshot(j);
        setLoading(false); // ukaž cached data hned
      }
    } catch { /* ignore */ }
    // 2) Background refresh — bez loaderu pokud máme cache
    load(false);
    /* eslint-disable-next-line */
  }, []);

  const askInit = () => setInitPending(true);

  const initFromDefaults = async () => {
    setInitPending(false);
    setLoading(true);
    try {
      const r = await apiFetch("/api/pravidla", {
        method: "POST",
        body: JSON.stringify({ action: "init" }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Init selhal");
      setData(j.data || {});
      setInitialized(true);
      setDefaults(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // bypassTick — vynucuje re-render kazdou minutu, aby countdown badge fungoval
  const [, setBypassTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setBypassTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  // Krok 1: klik na Uložit sekci → bypass check → buď přímý save, nebo dialog.
  // Bypass je per-sekce (save:<sectionKey>), takže potvrzení v jedné sekci
  // neskočí jinou.
  const askSaveConfirm = (sectionKey, payload) => {
    const sec = SECTIONS.find((s) => s.key === sectionKey);
    if (isConfirmBypassed(`save:${sectionKey}`)) {
      executeSave(sectionKey, payload);
      return;
    }
    setSavePending({ sectionKey, payload, sectionLabel: sec?.title || sectionKey });
  };

  // Vyclenene save flow (volaji jak bypass, tak dialog).
  const executeSave = async (sectionKey, payload) => {
    setSaving(sectionKey);
    setError(null);
    try {
      const r = await apiFetch("/api/pravidla", {
        method: "POST",
        body: JSON.stringify({ action: "updateSection", section: sectionKey, payload }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Save selhal");
      setData((d) => ({ ...d, [sectionKey]: j.data }));
      setDirty((s) => ({ ...s, [sectionKey]: false }));
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  // Krok 2: potvrzeno → reálné uložení
  const saveSectionConfirmed = async () => {
    if (!savePending) return;
    const { sectionKey, payload } = savePending;
    setSavePending(null);
    await executeSave(sectionKey, payload);
  };

  // Manualni zruseni 60min bypassu pro konkretni sekci.
  const cancelSaveBypass = (sectionKey) => {
    clearConfirmBypass(`save:${sectionKey}`);
    setBypassTick((n) => n + 1);
  };

  // Update lokální stav sekce (mark dirty)
  const updateSectionLocal = (sectionKey, nextSection) => {
    setData((d) => ({ ...d, [sectionKey]: nextSection }));
    setDirty((s) => ({ ...s, [sectionKey]: true }));
  };

  const toggle = (k) => setExpanded((s) => ({ ...s, [k]: !s[k] }));

  if (loading && !data) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        <div className="text-xs text-slate-400">Načítám pravidla a výjimky…</div>
        <SkeletonSections count={7} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <ConfirmDialog
        open={!!savePending}
        title="Uložit změny v sekci?"
        description={
          savePending
            ? `Opravdu uložit změny v sekci "${savePending.sectionLabel}"? Změny se zaznamenají do audit logu a okamžitě začnou platit pro automatizace.`
            : ""
        }
        confirmLabel="Uložit"
        cancelLabel="Zrušit"
        variant="save"
        onConfirm={saveSectionConfirmed}
        onCancel={() => setSavePending(null)}
        loading={!!saving}
        actionKey={savePending ? `save:${savePending.sectionKey}` : undefined}
        bypassMinutes={60}
      />

      <ConfirmDialog
        open={initPending}
        title="Inicializovat pravidla?"
        description="Naplnit Blobs storage z dnešních hardcoded hodnot. Existující záznamy se nepřepíší."
        confirmLabel="Inicializovat"
        cancelLabel="Zrušit"
        variant="primary"
        onConfirm={initFromDefaults}
        onCancel={() => setInitPending(false)}
        loading={loading}
      />

      <GradientHeader
        variant="emerald"
        eyebrow="Centralizace fakturačních a provozních pravidel"
        title={
          <span className="flex items-center gap-2">
            Pravidla a výjimky
            <InfoTip text="Centrální editor pravidel uložených v Netlify Blobs. 7 sekcí (3 fakturační + 4 provozní). Status Mirror znamená, že kód dnes čte vlastní hardcoded mirror. Refactor proběhne v týdnu 2 migrace. Změny zde se logují do audit logu." />
          </span>
        }
        subtitle="Hardcoded konstanty na jednom místě. Každá úprava vyžaduje potvrzení a zaznamená se do audit logu."
        icon={SettingsIcon}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {!initialized && defaults && (
        <Card>
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-slate-900 mb-1">Pravidla zatím nejsou inicializovaná</div>
              <p className="text-sm text-slate-600 mb-3">
                Blobs storage je prázdné. Mohu naplnit dnešními hardcoded hodnotami z kódu (tabulka aliasů, sazby trenérů, výjimky bonusů, minima tréninků a mapování balíčků). Po inicializaci je můžeš upravovat zde, audit historie se uchová.
              </p>
              <button
                onClick={askInit}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium rounded-xl text-sm min-h-[44px] sm:min-h-0"
              >
                Inicializovat z dnešních hodnot
              </button>
              <p className="text-xs text-slate-400 mt-3">
                Tip: po inicializaci si můžeš data prohlédnout a opravit překlepy, přidat nové aliasy, atd. Vše je auditováno.
              </p>
            </div>
          </div>
        </Card>
      )}

      {SECTIONS.map((sec) => {
        const sectionData = data && data[sec.key];
        const isExpanded = !!expanded[sec.key];
        const status = statusIndex[sec.key];
        const isDirty = !!dirty[sec.key];
        const isSaving = saving === sec.key;
        return (
          <Card key={sec.key} className={isDirty ? "ring-2 ring-amber-200" : ""}>
            <SectionHeader
              section={sec}
              expanded={isExpanded}
              onToggle={() => toggle(sec.key)}
              status={status}
              dirty={isDirty}
              count={(sectionData && (sectionData.items || []).filter((i) => i.aktivni !== false).length) || 0}
              description={sectionData?.description}
            />
            {isExpanded && sectionData && (
              <SectionBody
                section={sec}
                data={sectionData}
                onChange={(next) => updateSectionLocal(sec.key, next)}
                onSave={() => askSaveConfirm(sec.key, sectionData)}
                saving={isSaving}
                dirty={isDirty}
                disabled={!initialized}
                bypassUntil={getConfirmBypassUntil(`save:${sec.key}`)}
                onCancelBypass={() => cancelSaveBypass(sec.key)}
              />
            )}
          </Card>
        );
      })}
    </div>
  );
}

function SectionHeader({ section, expanded, onToggle, status, dirty, count, description }) {
  const Icon = section.icon || SettingsIcon;
  const ChevronIcon = expanded ? ChevronDown : ChevronRight;
  const groupLabel = section.group === "fakturace" ? "Fakturace" : "Provoz";
  const groupTone = section.group === "fakturace" ? "purple" : "blue";
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-3 -mx-1 -my-1 p-1 rounded hover:bg-slate-50 text-left min-h-[44px] sm:min-h-0"
    >
      <div className="flex items-center gap-3 min-w-0">
        <ChevronIcon className="w-5 h-5 text-slate-400 flex-shrink-0" />
        <Icon className="w-5 h-5 text-slate-500 flex-shrink-0" />
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
            {section.title}
            {/* Description je popis pravidla — zobrazí se jako info tooltip vedle nadpisu místo
                samostatného odstavce v body. Šetří vertikální místo, dostupné pro každého kdo
                najede na "?" ikonu. */}
            {description && (
              <span onClick={(e) => e.stopPropagation()} className="inline-flex">
                <InfoTip text={description} />
              </span>
            )}
            {dirty && <Pill tone="amber">Neuloženo</Pill>}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <Pill tone={groupTone}>{groupLabel}</Pill>
            {status && <Pill tone="amber">Mirror, refactor v {status.refactorVe || "Týden 2"}</Pill>}
            <span>{count} aktivních záznamů</span>
          </div>
        </div>
      </div>
    </button>
  );
}

function SectionBody({
  section,
  data,
  onChange,
  onSave,
  saving,
  dirty,
  disabled,
  bypassUntil,
  onCancelBypass,
}) {
  const minLeft = bypassUntil ? Math.max(1, Math.round((bypassUntil - Date.now()) / 60000)) : 0;
  return (
    <div className="mt-4 pt-4 border-t border-slate-100">
      {/* data.description se zobrazuje jako tooltip vedle nadpisu (SectionHeader),
          ne jako samostatný odstavec — šetří vertikální místo a popisek je dostupný
          přes "?" ikonku. */}
      {section.customRender ? (
        <CustomEditor section={section} data={data} onChange={onChange} disabled={disabled} />
      ) : (
        <SimpleTableEditor section={section} data={data} onChange={onChange} disabled={disabled} />
      )}

      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="text-xs text-slate-400">
          {data.updatedAt ? `Naposled uloženo ${formatDateTime(data.updatedAt)} (${data.updatedBy || "neznámý"})` : null}
        </div>
        <div className="flex items-center gap-2">
          {bypassUntil > 0 && (
            <button
              onClick={onCancelBypass}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 transition min-h-[44px] sm:min-h-0"
              title={`Bez potvrzení dalších ${minLeft} min. Klikni pro zrušení.`}
            >
              Bez ptaní {minLeft}m
              <span aria-hidden="true">×</span>
            </button>
          )}
          <button
            onClick={onSave}
            disabled={!dirty || saving || disabled}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium transition-colors ${
              !dirty || saving || disabled
                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                : "bg-amber-500 hover:bg-amber-600 text-slate-900"
            }`}
          >
            <Save className="w-4 h-4" />
            {saving ? "Ukládám." : "Uložit sekci"}
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Simple table editor (90% sekcí)
// =====================================================================
function SimpleTableEditor({ section, data, onChange, disabled }) {
  const items = data.items || [];
  // Confirm dialog pro smazani radku — sjednoceny styling (variant=danger).
  const [deletePending, setDeletePending] = useState(null); // { idx, label }

  const updateItem = (idx, field, value) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [field]: value } : it));
    onChange({ ...data, items: next });
  };

  const askRemove = (idx) => {
    const it = items[idx];
    // Pokus o sestaveni "lidskeho" labelu radku z prvniho text sloupce.
    const firstTextCol = section.columns.find((c) => c.type === "text");
    const label = (firstTextCol && it && it[firstTextCol.field]) || `řádek #${idx + 1}`;
    setDeletePending({ idx, label: String(label) });
  };

  const confirmRemove = () => {
    if (!deletePending) return;
    const next = items.filter((_, i) => i !== deletePending.idx);
    onChange({ ...data, items: next });
    setDeletePending(null);
  };

  const addItem = () => {
    const next = [...items, { id: newId(), ...section.newRowDefaults }];
    onChange({ ...data, items: next });
  };

  return (
    <div>
      <ConfirmDialog
        open={!!deletePending}
        title="Smazat řádek?"
        description={deletePending ? `Opravdu smazat "${deletePending.label}"? Po uložení sekce se změna projeví.` : ""}
        confirmLabel="Smazat"
        cancelLabel="Zrušit"
        variant="danger"
        onConfirm={confirmRemove}
        onCancel={() => setDeletePending(null)}
      />
      <div className="overflow-x-auto -mx-4 sm:mx-0 rounded-xl border border-slate-200 bg-slate-50/30">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-100/60 border-b border-slate-200">
              {section.columns.map((col) => (
                <th key={col.field} className={`py-2.5 px-3 font-semibold ${col.width || ""}`}>{col.label}</th>
              ))}
              <th className="py-2.5 px-3 w-12" aria-label="Akce" />
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => {
              // Inactive řádky (aktivni=false) se vizuálně ztlumí — jasně vidíš co je vypnuté.
              const isInactive = it.aktivni === false;
              return (
                <tr
                  key={it.id || idx}
                  className={`group border-t border-slate-100 transition-colors hover:bg-blue-50/40 ${
                    isInactive ? "opacity-50 bg-slate-50/60" : "bg-white"
                  } ${idx % 2 === 1 && !isInactive ? "bg-slate-50/40" : ""}`}
                >
                  {section.columns.map((col) => (
                    <td key={col.field} className="py-2 px-3 align-middle">
                      <CellEditor
                        type={col.type}
                        value={it[col.field]}
                        onChange={(v) => updateItem(idx, col.field, v)}
                        disabled={disabled}
                      />
                    </td>
                  ))}
                  <td className="py-2 px-3 align-middle">
                    <button
                      onClick={() => askRemove(idx)}
                      disabled={disabled}
                      className="p-1.5 rounded-lg text-slate-300 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-30 transition-colors group-hover:text-slate-500"
                      title="Smazat řádek"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr>
                <td colSpan={section.columns.length + 1} className="py-6 px-2 text-center text-slate-400 italic bg-white">
                  Žádné záznamy. Přidej nový řádek tlačítkem dole.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <button
        onClick={addItem}
        disabled={disabled}
        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-xl bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors min-h-[44px] sm:min-h-0"
      >
        <Plus className="w-4 h-4" /> Přidat řádek
      </button>
    </div>
  );
}

// Modern input style — sjednocený přes Cell editor (text/number/date) + lepší focus ring + hover.
const INPUT_BASE = "w-full text-sm bg-white border border-slate-200 rounded-md px-2.5 py-1.5 transition-all hover:border-slate-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-400";

function CellEditor({ type, value, onChange, disabled }) {
  if (type === "boolean") {
    // Custom toggle switch — viditelnější než plain checkbox. Zelená když ON, šedá když OFF.
    return (
      <button
        type="button"
        role="switch"
        aria-checked={!!value}
        onClick={() => !disabled && onChange(!value)}
        disabled={disabled}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          value ? "bg-emerald-500" : "bg-slate-300"
        }`}
        title={value ? "Aktivní (kliknutím vypneš)" : "Neaktivní (kliknutím zapneš)"}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform ${
            value ? "translate-x-5" : "translate-x-1"
          }`}
        />
      </button>
    );
  }
  if (type === "number") {
    return (
      <input
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        disabled={disabled}
        className={INPUT_BASE}
      />
    );
  }
  if (type === "date") {
    return (
      <input
        type="date"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={INPUT_BASE}
      />
    );
  }
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={INPUT_BASE}
    />
  );
}

// =====================================================================
// Custom editory (item-based aliasy + DPH navýšení)
// =====================================================================
function CustomEditor({ section, data, onChange, disabled }) {
  if (section.key === "fakturacni_aliasy_item_based") {
    return <ItemBasedEditor data={data} onChange={onChange} disabled={disabled} />;
  }
  if (section.key === "fakturacni_dph_navyseni") {
    return <DphEditor data={data} onChange={onChange} disabled={disabled} />;
  }
  return <div className="text-sm text-red-500">Neznámý custom editor: {section.key}</div>;
}

function ItemBasedEditor({ data, onChange, disabled }) {
  const items = data.items || [];
  // Confirm dialogy pro destruktivni akce (smazat pravidlo / smazat firmu).
  // Sjednoceny styling (variant=danger).
  const [removeRulePending, setRemoveRulePending] = useState(null); // { idx, ruleIdx, label }
  const [removeFirmaPending, setRemoveFirmaPending] = useState(null); // { idx, label }

  const updateItem = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange({ ...data, items: next });
  };

  const updateRule = (idx, ruleIdx, patch) => {
    const next = items.map((it, i) => {
      if (i !== idx) return it;
      const rules = it.rules.map((r, ri) => (ri === ruleIdx ? { ...r, ...patch } : r));
      return { ...it, rules };
    });
    onChange({ ...data, items: next });
  };

  const addRule = (idx) => {
    const next = items.map((it, i) => {
      if (i !== idx) return it;
      return { ...it, rules: [...(it.rules || []), { obsahuje: "", sheetKlient: "" }] };
    });
    onChange({ ...data, items: next });
  };

  const askRemoveRule = (idx, ruleIdx) => {
    const r = items[idx]?.rules?.[ruleIdx];
    const label = (r && (r.obsahuje || r.sheetKlient)) || `pravidlo #${ruleIdx + 1}`;
    setRemoveRulePending({ idx, ruleIdx, label: String(label) });
  };
  const confirmRemoveRule = () => {
    if (!removeRulePending) return;
    const { idx, ruleIdx } = removeRulePending;
    const next = items.map((it, i) => {
      if (i !== idx) return it;
      return { ...it, rules: it.rules.filter((_, ri) => ri !== ruleIdx) };
    });
    onChange({ ...data, items: next });
    setRemoveRulePending(null);
  };

  const askRemoveItem = (idx) => {
    const it = items[idx];
    const label = (it && it.fapiJmeno) || `firma #${idx + 1}`;
    setRemoveFirmaPending({ idx, label: String(label) });
  };
  const confirmRemoveItem = () => {
    if (!removeFirmaPending) return;
    const next = items.filter((_, i) => i !== removeFirmaPending.idx);
    onChange({ ...data, items: next });
    setRemoveFirmaPending(null);
  };

  const addItem = () => {
    onChange({
      ...data,
      items: [...items, { id: newId(), fapiJmeno: "", rules: [], defaultSheetKlient: "", aktivni: true }],
    });
  };

  return (
    <div className="space-y-4">
      <ConfirmDialog
        open={!!removeRulePending}
        title="Smazat pravidlo?"
        description={removeRulePending ? `Opravdu smazat pravidlo "${removeRulePending.label}"? Po uložení sekce se změna projeví.` : ""}
        confirmLabel="Smazat"
        cancelLabel="Zrušit"
        variant="danger"
        onConfirm={confirmRemoveRule}
        onCancel={() => setRemoveRulePending(null)}
      />
      <ConfirmDialog
        open={!!removeFirmaPending}
        title="Smazat celou firmu?"
        description={removeFirmaPending ? `Opravdu smazat firmu "${removeFirmaPending.label}" včetně všech jejích pravidel? Po uložení sekce se změna projeví.` : ""}
        confirmLabel="Smazat"
        cancelLabel="Zrušit"
        variant="danger"
        onConfirm={confirmRemoveItem}
        onCancel={() => setRemoveFirmaPending(null)}
      />
      {items.map((it, idx) => (
        <div key={it.id || idx} className="border border-slate-200 rounded-xl p-3">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="text-xs text-slate-500">
                FAPI jméno (firma)
                <input
                  type="text"
                  value={it.fapiJmeno || ""}
                  onChange={(e) => updateItem(idx, { fapiJmeno: e.target.value })}
                  disabled={disabled}
                  className="field mt-0.5 w-full disabled:bg-slate-50"
                />
              </label>
              <label className="text-xs text-slate-500">
                Default klient (bez shody pravidla)
                <input
                  type="text"
                  value={it.defaultSheetKlient || ""}
                  onChange={(e) => updateItem(idx, { defaultSheetKlient: e.target.value })}
                  disabled={disabled}
                  className="field mt-0.5 w-full disabled:bg-slate-50"
                />
              </label>
            </div>
            <label className="text-xs text-slate-500 flex items-center gap-2 mt-3">
              <input
                type="checkbox"
                checked={!!it.aktivni}
                onChange={(e) => updateItem(idx, { aktivni: e.target.checked })}
                disabled={disabled}
              />
              Aktivní
            </label>
            <button
              onClick={() => askRemoveItem(idx)}
              disabled={disabled}
              className="p-1 text-slate-400 hover:text-red-600 disabled:opacity-30 mt-3"
              title="Smazat firmu"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="ml-3 pl-3 border-l-2 border-slate-200">
            <div className="text-xs text-slate-500 mb-1">Pravidla (text v položce → klient):</div>
            {(it.rules || []).map((r, ri) => (
              <div key={ri} className="flex items-center gap-2 mb-1">
                <input
                  type="text"
                  placeholder="obsahuje text"
                  value={r.obsahuje || ""}
                  onChange={(e) => updateRule(idx, ri, { obsahuje: e.target.value })}
                  disabled={disabled}
                  className="field w-44 disabled:bg-slate-50"
                />
                <span className="text-xs text-slate-400">→</span>
                <input
                  type="text"
                  placeholder="klient v adminu"
                  value={r.sheetKlient || ""}
                  onChange={(e) => updateRule(idx, ri, { sheetKlient: e.target.value })}
                  disabled={disabled}
                  className="field flex-1 disabled:bg-slate-50"
                />
                <button
                  onClick={() => askRemoveRule(idx, ri)}
                  disabled={disabled}
                  className="p-1 text-slate-400 hover:text-red-600 disabled:opacity-30"
                  title="Smazat pravidlo"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button
              onClick={() => addRule(idx)}
              disabled={disabled}
              className="text-xs text-blue-600 hover:text-blue-700 disabled:opacity-30 flex items-center gap-1 mt-1"
            >
              <Plus className="w-3 h-3" /> Přidat pravidlo
            </button>
          </div>
        </div>
      ))}
      {items.length === 0 && (
        <div className="text-center text-slate-400 py-3 text-sm">Žádné firmy. Přidej novou.</div>
      )}
      <button
        onClick={addItem}
        disabled={disabled}
        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-30"
      >
        <Plus className="w-4 h-4" /> Přidat firmu
      </button>
    </div>
  );
}

function DphEditor({ data, onChange, disabled }) {
  const items = data.items || [];
  const vyjimky = data.vyjimky || [];
  const vatRate = data.vatRate ?? 0.21;
  // Confirm dialogy pro smazani klienta a vyjimky.
  const [removeKlientPending, setRemoveKlientPending] = useState(null);
  const [removeVyjimkaPending, setRemoveVyjimkaPending] = useState(null);

  const updateItems = (next) => onChange({ ...data, items: next });
  const updateVyjimky = (next) => onChange({ ...data, vyjimky: next });

  const addItem = () => updateItems([...items, { id: newId(), klient: "", firma: "", aktivni: true }]);
  const updateItem = (idx, patch) => updateItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const askRemoveItem = (idx) => {
    const it = items[idx];
    const label = (it && it.klient) || `klient #${idx + 1}`;
    setRemoveKlientPending({ idx, label: String(label) });
  };
  const confirmRemoveItem = () => {
    if (!removeKlientPending) return;
    updateItems(items.filter((_, i) => i !== removeKlientPending.idx));
    setRemoveKlientPending(null);
  };

  const addVyjimka = () => updateVyjimky([...vyjimky, { id: newId(), klient: "", duvod: "", aktivni: true }]);
  const updateVyjimka = (idx, patch) => updateVyjimky(vyjimky.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const askRemoveVyjimka = (idx) => {
    const it = vyjimky[idx];
    const label = (it && it.klient) || `výjimka #${idx + 1}`;
    setRemoveVyjimkaPending({ idx, label: String(label) });
  };
  const confirmRemoveVyjimka = () => {
    if (!removeVyjimkaPending) return;
    updateVyjimky(vyjimky.filter((_, i) => i !== removeVyjimkaPending.idx));
    setRemoveVyjimkaPending(null);
  };

  return (
    <div className="space-y-4">
      <ConfirmDialog
        open={!!removeKlientPending}
        title="Smazat klienta?"
        description={removeKlientPending ? `Opravdu smazat řádek "${removeKlientPending.label}"? Po uložení sekce se změna projeví.` : ""}
        confirmLabel="Smazat"
        cancelLabel="Zrušit"
        variant="danger"
        onConfirm={confirmRemoveItem}
        onCancel={() => setRemoveKlientPending(null)}
      />
      <ConfirmDialog
        open={!!removeVyjimkaPending}
        title="Smazat výjimku?"
        description={removeVyjimkaPending ? `Opravdu smazat výjimku "${removeVyjimkaPending.label}"? Po uložení sekce se změna projeví.` : ""}
        confirmLabel="Smazat"
        cancelLabel="Zrušit"
        variant="danger"
        onConfirm={confirmRemoveVyjimka}
        onCancel={() => setRemoveVyjimkaPending(null)}
      />
      {/* Sazba DPH — výrazně oddělené, lepší input. */}
      <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
        <label className="text-sm font-semibold text-slate-700">Sazba DPH</label>
        <input
          type="number"
          step="0.01"
          value={vatRate}
          onChange={(e) => onChange({ ...data, vatRate: Number(e.target.value) })}
          disabled={disabled}
          className={`${INPUT_BASE} w-24`}
        />
        <span className="text-sm font-medium text-slate-500">({Math.round(vatRate * 100)}%)</span>
      </div>

      {/* Lista 1: Klienti BEZ DPH (navyšovat) */}
      <div>
        <div className="font-semibold text-slate-900 text-sm mb-2 flex items-center gap-2">
          <span className="inline-block w-1 h-4 bg-emerald-500 rounded" />
          Klienti, jejichž částka v kartě klienta je BEZ DPH (navyšovat o {Math.round(vatRate * 100)}%)
        </div>
        <DphRulesTable
          rows={items}
          cols={[
            { field: "klient", label: "Klient", width: "w-56" },
            { field: "firma", label: "Firma / důvod" },
          ]}
          onUpdate={updateItem}
          onAskRemove={askRemoveItem}
          disabled={disabled}
        />
        <button
          onClick={addItem}
          disabled={disabled}
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors min-h-[44px] sm:min-h-0"
        >
          <Plus className="w-4 h-4" /> Přidat klienta
        </button>
      </div>

      {/* Lista 2: Výjimky (NENAVYŠOVAT) — odlišná akcent barva pro vizuální kontrast */}
      <div>
        <div className="font-semibold text-slate-900 text-sm mb-2 flex items-center gap-2">
          <span className="inline-block w-1 h-4 bg-amber-500 rounded" />
          Výjimky (NEnavyšovat, např. částka už obsahuje DPH)
        </div>
        <DphRulesTable
          rows={vyjimky}
          cols={[
            { field: "klient", label: "Klient", width: "w-56" },
            { field: "duvod", label: "Důvod" },
          ]}
          onUpdate={updateVyjimka}
          onAskRemove={askRemoveVyjimka}
          disabled={disabled}
        />
        <button
          onClick={addVyjimka}
          disabled={disabled}
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-xl bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors min-h-[44px] sm:min-h-0"
        >
          <Plus className="w-4 h-4" /> Přidat výjimku
        </button>
      </div>
    </div>
  );
}

// Sjednocena tabulka pro DPH navyseni (interni helper). Stejny vizual jako SimpleTableEditor:
// zebra rows + hover + dim inactive + toggle switch.
function DphRulesTable({ rows, cols, onUpdate, onAskRemove, disabled }) {
  return (
    <div className="overflow-x-auto -mx-4 sm:mx-0 rounded-xl border border-slate-200 bg-slate-50/30">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-100/60 border-b border-slate-200">
            {cols.map((c) => (
              <th key={c.field} className={`py-2.5 px-3 font-semibold ${c.width || ""}`}>{c.label}</th>
            ))}
            <th className="py-2.5 px-3 font-semibold w-20">Aktivní</th>
            <th className="py-2.5 px-3 w-12" />
          </tr>
        </thead>
        <tbody>
          {rows.map((it, idx) => {
            const isInactive = it.aktivni === false;
            return (
              <tr
                key={it.id || idx}
                className={`group border-t border-slate-100 transition-colors hover:bg-blue-50/40 ${
                  isInactive ? "opacity-50 bg-slate-50/60" : "bg-white"
                } ${idx % 2 === 1 && !isInactive ? "bg-slate-50/40" : ""}`}
              >
                {cols.map((c) => (
                  <td key={c.field} className="py-2 px-3 align-middle">
                    <input
                      type="text"
                      value={it[c.field] || ""}
                      onChange={(e) => onUpdate(idx, { [c.field]: e.target.value })}
                      disabled={disabled}
                      className={INPUT_BASE}
                    />
                  </td>
                ))}
                <td className="py-2 px-3 align-middle">
                  <CellEditor
                    type="boolean"
                    value={it.aktivni}
                    onChange={(v) => onUpdate(idx, { aktivni: v })}
                    disabled={disabled}
                  />
                </td>
                <td className="py-2 px-3 align-middle">
                  <button
                    onClick={() => onAskRemove(idx)}
                    disabled={disabled}
                    className="p-1.5 rounded-lg text-slate-300 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-30 transition-colors group-hover:text-slate-500"
                    title="Smazat řádek"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={cols.length + 2} className="py-6 px-2 text-center text-slate-400 italic bg-white">
                Žádné záznamy. Přidej nový řádek tlačítkem dole.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
}
